/**
 * Minimum Order Modal
 * Shows recommendations when cart is below threshold configured in FS Toolkit
 */

class MinimumOrderModal {
  constructor() {
    this.modal = document.getElementById('MinimumOrderModal');
    this.config = window.FSMinimumOrderConfig || {};
    this.sessionKey = 'minimumOrderModalShownThisPageLoad';
    this.threshold = 0; // Will be fetched from FS Toolkit
    this.hiddenVariantId = '15777326235997';

    if (!this.modal || !this.config.isCartPage) return;

    this.itemsList = document.getElementById('MinimumOrderItems');
    this.loadingEl = document.getElementById('MinimumOrderLoading');
    this.minimumAmountEl = this.modal.querySelector('[data-minimum-amount]');
    this.amountRemainingEl = this.modal.querySelector('[data-amount-remaining]');
    this.checkoutButton = document.querySelector('[data-checkout-button], button[name="checkout"], #checkout');

    // Product pool for replenishment
    this.productPool = []; // Reserve products not yet displayed
    this.displayedProductIds = new Set(); // Track what's currently shown
    this.addedProductIds = new Set(); // Track products added to cart from modal
    this.maxPoolSize = 15; // Maximum products to keep in pool
    this.initialDisplayCount = 4; // How many to show initially

    this.init();
  }

  async init() {
    // Fetch config from FS Toolkit
    await this.fetchConfig();

    // If no threshold configured, don't show modal
    if (!this.threshold || this.threshold <= 0) return;

    // Check if we should show the modal
    if (await this.shouldShowModal()) {
      this.updateModalContent();
      await this.fetchRecommendations();
      this.showModal();
      // Mark that we've shown the modal this page load
      sessionStorage.setItem(this.sessionKey, 'true');
    }

    // Update checkout button state
    await this.updateCheckoutButton();

    this.bindEvents();
    this.subscribeToCartUpdates();
  }

  async fetchConfig() {
    try {
      const res = await fetch('/apps/delivery-date/config');
      if (!res.ok) throw new Error('Failed to fetch config');
      const config = await res.json();
      this.threshold = config.minimumOrderSubtotalPence || 0;
    } catch (err) {
      console.warn('MinimumOrderModal: Failed to fetch FS Toolkit config', err);
      // Fallback to default £30
      this.threshold = 3000;
    }
  }

  async shouldShowModal() {
    const cart = await this.fetchCart();
    if (!cart) return false;

    const visibleTotal = this.calculateVisibleTotal(cart);
    const shownThisPageLoad = sessionStorage.getItem(this.sessionKey) === 'true';

    // Show if cart has items, is below threshold, and hasn't been shown this page load
    return visibleTotal > 0 && visibleTotal < this.threshold && !shownThisPageLoad;
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (err) {
      console.warn('MinimumOrderModal: Failed to fetch cart', err);
      return null;
    }
  }

  calculateVisibleTotal(cart) {
    let hiddenTotal = 0;

    for (const item of cart.items) {
      if (String(item.variant_id) === this.hiddenVariantId ||
          String(item.product_id) === this.hiddenVariantId) {
        hiddenTotal += item.final_line_price;
      }
    }

    return cart.total_price - hiddenTotal;
  }

  updateModalContent() {
    if (this.minimumAmountEl) {
      this.minimumAmountEl.textContent = this.formatMoney(this.threshold);
    }

    const visibleTotal = this.config.currentTotal || 0;
    const remaining = Math.max(this.threshold - visibleTotal, 0);

    if (this.amountRemainingEl) {
      this.amountRemainingEl.textContent = this.formatMoney(remaining);
    }
  }

  showModal() {
    setTimeout(() => {
      if (this.modal) {
        this.modal.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        const dialog = this.modal.querySelector('[role="dialog"]');
        if (dialog) dialog.focus();
      }
    }, 300);
  }

  hideModal() {
    if (this.modal) {
      this.modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }

  bindEvents() {
    // Close button
    const closeBtn = this.modal.querySelector('[id^="ModalClose-"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hideModal());
    }

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });

    // ESC key to close
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.modal.hasAttribute('open')) {
        this.hideModal();
      }
    });

    // Event delegation for add buttons
    this.itemsList?.addEventListener('click', async (e) => {
      const btn = e.target.closest('.fs-minimum-order-modal__item-add');
      if (btn && !btn.disabled) {
        e.preventDefault();
        await this.addToCart(btn);
      }
    });

    // Event delegation for variant selects
    this.itemsList?.addEventListener('change', (e) => {
      const select = e.target.closest('.fs-minimum-order-modal__variant-select');
      if (select) {
        this.updateAddButtonVariant(select);
      }
    });
  }

  subscribeToCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        await this.handleCartUpdate();
      });
    }
  }

  async handleCartUpdate() {
    const cart = await this.fetchCart();
    if (!cart) return;

    const visibleTotal = this.calculateVisibleTotal(cart);
    const remaining = Math.max(this.threshold - visibleTotal, 0);

    // Update amount remaining display
    if (this.amountRemainingEl) {
      this.amountRemainingEl.textContent = this.formatMoney(remaining);
    }

    // Update checkout button
    await this.updateCheckoutButton(visibleTotal);

    // If threshold met, close modal
    if (visibleTotal >= this.threshold) {
      // Clear the session flag so if they remove items, modal can show again
      sessionStorage.removeItem(this.sessionKey);
      this.hideModal();
    }
    // If cart dropped below threshold and modal not open, show it
    else if (visibleTotal > 0 && visibleTotal < this.threshold && !this.modal.hasAttribute('open')) {
      this.updateModalContent();
      await this.fetchRecommendations();
      this.showModal();
    }
  }

  async updateCheckoutButton(visibleTotal) {
    if (!this.checkoutButton) return;

    if (visibleTotal === undefined) {
      const cart = await this.fetchCart();
      if (cart) {
        visibleTotal = this.calculateVisibleTotal(cart);
      }
    }

    if (visibleTotal < this.threshold) {
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('aria-disabled', 'true');
      this.checkoutButton.title = `Minimum order of ${this.formatMoney(this.threshold)} required`;
    } else {
      this.checkoutButton.disabled = false;
      this.checkoutButton.removeAttribute('aria-disabled');
      this.checkoutButton.title = '';
    }
  }

  async fetchRecommendations() {
    const cartProductIds = this.config.cartProductIds || [];
    if (cartProductIds.length === 0) {
      this.showEmptyState();
      return;
    }

    // Build a set of IDs to exclude (already in cart + already added from modal)
    const seenIds = new Set([
      ...cartProductIds.map(String),
      ...Array.from(this.addedProductIds).map(String)
    ]);

    // Fetch recommendations from multiple cart items to build a larger pool
    const allRecommendations = [];

    // Fetch from more cart items to get more variety
    for (const productId of cartProductIds.slice(0, 5)) {
      if (allRecommendations.length >= this.maxPoolSize) break;

      try {
        const res = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`
        );
        if (!res.ok) continue;

        const data = await res.json();
        if (!data.products) continue;

        for (const product of data.products) {
          if (seenIds.has(String(product.id))) continue;
          if (!product.available) continue;

          seenIds.add(String(product.id));
          allRecommendations.push(product);

          if (allRecommendations.length >= this.maxPoolSize) break;
        }
      } catch (err) {
        console.warn('MinimumOrderModal: Failed to fetch recommendations', err);
      }
    }

    // If we still don't have enough, try fetching from collection or best sellers
    if (allRecommendations.length < this.maxPoolSize) {
      await this.fetchAdditionalProducts(allRecommendations, seenIds);
    }

    // Split into initial display and reserve pool
    const initialProducts = allRecommendations.slice(0, this.initialDisplayCount);
    this.productPool = allRecommendations.slice(this.initialDisplayCount);

    // Track displayed products
    initialProducts.forEach(p => this.displayedProductIds.add(String(p.id)));

    this.renderRecommendations(initialProducts);
  }

  async fetchAdditionalProducts(existingProducts, seenIds) {
    // Try to fetch from best-selling or popular products
    try {
      const res = await fetch('/collections/all/products.json?limit=20');
      if (!res.ok) return;

      const data = await res.json();
      if (!data.products) return;

      for (const product of data.products) {
        if (existingProducts.length >= this.maxPoolSize) break;
        if (seenIds.has(String(product.id))) continue;
        if (!product.available) continue;

        seenIds.add(String(product.id));
        existingProducts.push(product);
      }
    } catch (err) {
      console.warn('MinimumOrderModal: Failed to fetch additional products', err);
    }
  }

  showEmptyState() {
    if (this.loadingEl) {
      this.loadingEl.innerHTML = '<p class="fs-minimum-order-modal__empty">Browse our collection to add items to your order.</p>';
    }
  }

  renderRecommendations(products) {
    if (!this.itemsList) return;

    if (products.length === 0) {
      this.showEmptyState();
      return;
    }

    this.itemsList.innerHTML = products.map(product => {
      const hasVariants = product.variants && product.variants.length > 1;
      const firstVariant = product.variants?.[0] || product;
      const imageUrl = product.featured_image || product.images?.[0] || '';
      const imageSrc = imageUrl ? (imageUrl.includes('?') ? `${imageUrl}&width=140` : `${imageUrl}?width=140`) : '';

      return `
        <li class="fs-minimum-order-modal__item" data-product-id="${product.id}">
          <a href="${product.url || '/products/' + product.handle}" class="fs-minimum-order-modal__item-image">
            <img
              src="${imageSrc || 'data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 70 70\"%3E%3Crect fill=\"%23ddd\" width=\"70\" height=\"70\"/%3E%3C/svg%3E'}"
              alt="${this.escapeHtml(product.title)}"
              loading="lazy"
              width="70"
              height="70"
            >
          </a>
          <div class="fs-minimum-order-modal__item-details">
            <a href="${product.url || '/products/' + product.handle}" class="fs-minimum-order-modal__item-title">
              ${this.escapeHtml(product.title)}
            </a>
            <p class="fs-minimum-order-modal__item-price" data-price="${firstVariant.price}">
              ${this.formatMoney(firstVariant.price)}
            </p>
          </div>
          <div class="fs-minimum-order-modal__item-actions">
            ${hasVariants ? this.renderVariantSelect(product) : ''}
            <button
              type="button"
              class="fs-minimum-order-modal__item-add"
              data-variant-id="${firstVariant.id}"
              ${!firstVariant.available ? 'disabled' : ''}
            >
              <span class="fs-minimum-order-modal__add-text">${firstVariant.available ? 'Add' : 'Sold out'}</span>
              <div class="loading__spinner">
                <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                  <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                </svg>
              </div>
            </button>
          </div>
        </li>
      `;
    }).join('');

    if (this.loadingEl) this.loadingEl.style.display = 'none';
    this.itemsList.style.display = 'flex';
  }

  renderVariantSelect(product) {
    const options = product.variants.map(v => `
      <option
        value="${v.id}"
        data-price="${v.price}"
        data-available="${v.available}"
        ${!v.available ? 'disabled' : ''}
      >
        ${this.escapeHtml(v.title)}${!v.available ? ' - Sold out' : ''}
      </option>
    `).join('');

    return `
      <select class="fs-minimum-order-modal__variant-select" data-product-id="${product.id}">
        ${options}
      </select>
    `;
  }

  updateAddButtonVariant(select) {
    const item = select.closest('.fs-minimum-order-modal__item');
    const addBtn = item?.querySelector('.fs-minimum-order-modal__item-add');
    const priceEl = item?.querySelector('[data-price]');
    const selectedOption = select.selectedOptions[0];

    if (!addBtn || !selectedOption) return;

    const variantId = selectedOption.value;
    const price = parseInt(selectedOption.dataset.price, 10);
    const available = selectedOption.dataset.available === 'true';

    addBtn.dataset.variantId = variantId;
    addBtn.disabled = !available;

    const textEl = addBtn.querySelector('.fs-minimum-order-modal__add-text');
    if (textEl) {
      textEl.textContent = available ? 'Add' : 'Sold out';
    }

    if (priceEl) {
      priceEl.textContent = this.formatMoney(price);
      priceEl.dataset.price = price;
    }
  }

  async addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('loading');

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }],
          sections: ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const result = await response.json();

      // Update cart sections if available
      if (result.sections) {
        this.updateCartSections(result.sections);
      }

      // Publish cart update event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { cartData: result });
      }

      // Update product IDs in config
      const productId = btn.closest('.fs-minimum-order-modal__item')?.dataset.productId;
      if (productId) {
        // Track this product as added
        this.addedProductIds.add(String(productId));
        this.displayedProductIds.delete(String(productId));

        if (this.config.cartProductIds) {
          this.config.cartProductIds.push(parseInt(productId));
        }
      }

      // Remove the item from recommendations (it's now in cart)
      const item = btn.closest('.fs-minimum-order-modal__item');
      if (item) {
        item.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        item.style.opacity = '0';
        item.style.transform = 'translateX(-20px)';
        setTimeout(() => {
          item.remove();
          // Replenish the list with a new product from the pool
          this.replenishList();
        }, 300);
      }

      // Check if we've met the threshold
      await this.checkThresholdMet();

    } catch (err) {
      console.error('MinimumOrderModal: Failed to add to cart', err);
      btn.classList.remove('loading');
    }
  }

  replenishList() {
    // If we have products in the pool, add one to the bottom
    if (this.productPool.length === 0) return;

    const nextProduct = this.productPool.shift();
    if (!nextProduct) return;

    // Mark as displayed
    this.displayedProductIds.add(String(nextProduct.id));

    // Create and append the new item
    const newItemHtml = this.renderSingleProduct(nextProduct);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newItemHtml;
    const newItem = tempDiv.firstElementChild;

    if (newItem && this.itemsList) {
      // Add with animation
      newItem.style.opacity = '0';
      newItem.style.transform = 'translateY(20px)';
      this.itemsList.appendChild(newItem);

      // Trigger animation
      requestAnimationFrame(() => {
        newItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        newItem.style.opacity = '1';
        newItem.style.transform = 'translateY(0)';
      });
    }
  }

  renderSingleProduct(product) {
    const hasVariants = product.variants && product.variants.length > 1;
    const firstVariant = product.variants?.[0] || product;
    const imageUrl = product.featured_image || product.images?.[0] || '';
    const imageSrc = imageUrl ? (imageUrl.includes('?') ? `${imageUrl}&width=140` : `${imageUrl}?width=140`) : '';

    return `
      <li class="fs-minimum-order-modal__item" data-product-id="${product.id}">
        <a href="${product.url || '/products/' + product.handle}" class="fs-minimum-order-modal__item-image">
          <img
            src="${imageSrc || 'data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 70 70\"%3E%3Crect fill=\"%23ddd\" width=\"70\" height=\"70\"/%3E%3C/svg%3E'}"
            alt="${this.escapeHtml(product.title)}"
            loading="lazy"
            width="70"
            height="70"
          >
        </a>
        <div class="fs-minimum-order-modal__item-details">
          <a href="${product.url || '/products/' + product.handle}" class="fs-minimum-order-modal__item-title">
            ${this.escapeHtml(product.title)}
          </a>
          <p class="fs-minimum-order-modal__item-price" data-price="${firstVariant.price}">
            ${this.formatMoney(firstVariant.price)}
          </p>
        </div>
        <div class="fs-minimum-order-modal__item-actions">
          ${hasVariants ? this.renderVariantSelect(product) : ''}
          <button
            type="button"
            class="fs-minimum-order-modal__item-add"
            data-variant-id="${firstVariant.id}"
            ${!firstVariant.available ? 'disabled' : ''}
          >
            <span class="fs-minimum-order-modal__add-text">${firstVariant.available ? 'Add' : 'Sold out'}</span>
            <div class="loading__spinner">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
              </svg>
            </div>
          </button>
        </div>
      </li>
    `;
  }

  updateCartSections(sections) {
    // Update main cart items
    if (sections['main-cart-items']) {
      const cartItemsEl = document.getElementById('main-cart-items');
      if (cartItemsEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['main-cart-items'], 'text/html');
        const newContent = doc.querySelector('#main-cart-items');
        if (newContent) {
          cartItemsEl.innerHTML = newContent.innerHTML;
        }
      }
    }

    // Update cart footer (totals)
    if (sections['main-cart-footer']) {
      const cartFooterEl = document.getElementById('main-cart-footer');
      if (cartFooterEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['main-cart-footer'], 'text/html');
        const newContent = doc.querySelector('#main-cart-footer');
        if (newContent) {
          cartFooterEl.innerHTML = newContent.innerHTML;
          // Re-find checkout button after section update
          this.checkoutButton = document.querySelector('[data-checkout-button], button[name="checkout"], #checkout');
        }
      }
    }

    // Update cart icon bubble
    if (sections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newContent = doc.querySelector('.shopify-section');
        if (newContent) {
          bubbleEl.innerHTML = newContent.innerHTML;
        }
      }
    }
  }

  async checkThresholdMet() {
    const cart = await this.fetchCart();
    if (!cart) return;

    const visibleTotal = this.calculateVisibleTotal(cart);
    this.config.currentTotal = visibleTotal;

    const remaining = Math.max(this.threshold - visibleTotal, 0);

    // Update display
    if (this.amountRemainingEl) {
      this.amountRemainingEl.textContent = this.formatMoney(remaining);
    }

    // Update checkout button
    await this.updateCheckoutButton(visibleTotal);

    // Close if threshold met
    if (visibleTotal >= this.threshold) {
      sessionStorage.removeItem(this.sessionKey);
      setTimeout(() => this.hideModal(), 500);
    }
  }

  formatMoney(cents) {
    const format = this.config.moneyFormat || '£{{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MinimumOrderModal());
} else {
  new MinimumOrderModal();
}
